import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {firstValueFrom, Observable} from 'rxjs';
import {AuthState, IUser} from '../../models/auth';
import {FormArray, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Store} from '@ngxs/store';
import {MiniSphereNamespace} from '../../namespaces/mini-sphere.namespace';
import {PLATFORM_CONFIG} from '../../config/platform.config';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {ENRICHING_ACTIVITIES} from '../../class/data';
import {PhysicalActivityLevelEnum} from '../../enums/record';
import {NzMarks} from 'ng-zorro-antd/slider';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {RecordMedicalPersonalAction} from '../../models/record-medical-personal';
import {RecordMedicalInheritedFamilyAction} from '../../models/record-medical-inherited-family';
import {ILabelValue} from '../../interfaces/utils';
import {cloneDeep as _cloneDeep, omit as _omit} from 'lodash';
import {IIngredient} from '../../models/ingredient';
import {MiniSphereState, RecordMiniSphereAction} from '../../models/record-mini-sphere/mini-sphere.redux';
import {ActivatedRoute, Params} from '@angular/router';
import {ServiceAction, ServiceState} from '../../models/service';
import {StorageService} from '../../services/storage/storage.service';
import {RecordHistoryPersonalMedicalComponent} from '../record-history-personal-medical/record-history-personal-medical.component';
import {RecordHistoryFamilialMedicalComponent} from '../record-history-familial-medical/record-history-familial-medical.component';
import {Loader} from '../../models/loader/loader.redux';
import {NzModalService} from 'ng-zorro-antd/modal';
import {SkeletonEnum} from '../../enums/skeleton';
import {SkeletonService} from '../../services/skeleton/skeleton.service';
import {NUTRITIONAL_REGIMENT} from '../../config/data/patient-record';
import {NzMessageService} from 'ng-zorro-antd/message';

@Component({
  selector: 'app-record-mini-sphere',
  templateUrl: './record-mini-sphere.component.html',
  styleUrls: ['./record-mini-sphere.component.scss'],
  animations: [fadeInAnimation]
})
export class RecordMiniSphereComponent implements OnInit, OnDestroy {
  isPatient$: Observable<boolean> = this.store.select<boolean>(AuthState.getIsPatient);
  platformConfig = PLATFORM_CONFIG;
  form: UntypedFormGroup;

  loadInfo: boolean;
  skeletonTemplate: any[] = [];
  frequencies: {label: string, value: number}[] = [];
  options: {label: string, value: number}[] = [];
  enrichingActivities = ENRICHING_ACTIVITIES;
  physicalActivityLevelEnum = PhysicalActivityLevelEnum;
  marks: NzMarks = {
    1: '',
    2: '',
    3: '',
    4: '',
    5: '',
    6: '',
    7: '',
    8: '',
    9: '',
    10: '',
    11: '',
    12: '',
    13: '',
    14: '',
    15: '',
  };
  nutritionalRegimen: ILabelValue[] = _cloneDeep(NUTRITIONAL_REGIMENT);
  excludedDislikeIngredients: MiniSphereNamespace.IIngredient[] = [];
  excludedAllergyIngredients: MiniSphereNamespace.IIngredient[] = [];

  serviceId: string;
  patientId: string;
  recordMiniSphereId: string;
  userLogged: IUser;

  modalAddAllergyVisible: boolean;
  @ViewChild(RecordHistoryPersonalMedicalComponent) recordHistoryPersonalMedicalComponent: RecordHistoryPersonalMedicalComponent;
  @ViewChild(RecordHistoryFamilialMedicalComponent) recordHistoryFamilialMedicalComponent: RecordHistoryFamilialMedicalComponent;
  constructor(
    private store: Store,
    public utilitiesService: UtilitiesService,
    private activatedRoute: ActivatedRoute,
    private storageService: StorageService,
    private modal: NzModalService,
    private skeletonService: SkeletonService,
    private messageService: NzMessageService
  ) {

  }

  ngOnInit(): void {
    this.initValues();
    this.initValuesHttp().then();
  }

  initValues(): void {
    this.skeletonTemplate = this.skeletonService.build(SkeletonEnum.ROWS);
    this.frequencies = [
      {
        label: 'No lo acostumbro',
        value: 0
      },
      {
        label: '1 a 2 días a la semana',
        value: 1
      },
      {
        label: '3 a 4 días a la semana',
        value: 3
      },
      {
        label: '5 a 7 días a la semana',
        value: 5
      }
    ];
    this.options = [
      {
        label: 'Nunca',
        value: 0
      },
      {
        label: 'Casi nunca',
        value: 1
      },
      {
        label: 'De vez en cuando',
        value: 2
      },
      {
        label: 'A menudo',
        value: 3
      },
      {
        label: 'Muy a menudo',
        value: 4
      }
    ];
    this.form = null;
  }

  async initValuesHttp(): Promise<void> {
    const params: Params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.patientId = params.patientId;
    let expertId: string;
    this.userLogged = _cloneDeep(this.storageService.getLocalItem<{ user: IUser, token: string }>('user')?.user);
    if (this.userLogged && this.userLogged.experts) {
      expertId = this.userLogged.experts[0]._id;
    } else if (this.userLogged && this.userLogged.patients) {
      const queryParams: Params = this.activatedRoute.snapshot.queryParams;
      expertId = queryParams.expert;
    }
    this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
    if (!this.serviceId) {
      await firstValueFrom(this.store.dispatch(new ServiceAction.GetActive(this.patientId, expertId)));
      this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
    }
    await firstValueFrom(this.store.dispatch(new RecordMedicalPersonalAction.Deploy(false)));
    await firstValueFrom(this.store.dispatch(new RecordMedicalInheritedFamilyAction.Deploy(false)));
    await firstValueFrom(this.store.dispatch(new RecordMiniSphereAction.GetActive(this.serviceId, this.patientId)));
    const recordMiniSphere: MiniSphereNamespace.IMiniSphere = _cloneDeep(await firstValueFrom(this.store.selectOnce(MiniSphereState.getRecordMiniSphere)));
    if (!recordMiniSphere) return;
    this.recordMiniSphereId = recordMiniSphere._id;
    this.setFormProperties(recordMiniSphere);
    this.loadInfo = true;
  }

  setFormProperties(miniSphere: MiniSphereNamespace.IMiniSphere): void {
    this.form = new UntypedFormGroup({
      // smoke listo
      smoke: new UntypedFormGroup({
        smoke: new UntypedFormControl({ value: miniSphere ? miniSphere.smoke.smoke || false : false, disabled: false}),
        smokeFrequency: new UntypedFormControl({ value: miniSphere ? miniSphere.smoke.smokeFrequency || 0 : undefined, disabled: false}),
        cigarettesPerDay: new UntypedFormControl({ value: miniSphere ? miniSphere.smoke.cigarettesPerDay || 0 : undefined, disabled: false}),
      }),
      // alcohol listo
      alcohol: new UntypedFormGroup({
        drinkAlcohol: new UntypedFormControl({ value: miniSphere ? miniSphere.alcohol.drinkAlcohol || false : false, disabled: false}),
        drinkAlcoholFrequency: new UntypedFormControl({ value: miniSphere ? miniSphere.alcohol.drinkAlcoholFrequency || 0 : undefined, disabled: false}),
        cupAlcoholPerDay: new UntypedFormControl({ value: miniSphere ? miniSphere.alcohol.cupAlcoholPerDay || 0 : undefined, disabled: false}),
      }),
      // sleep listo
      sleep: new UntypedFormGroup({
        whatTimeDoYouUsuallyGoToSleep: new UntypedFormControl({
          value: miniSphere ? miniSphere.sleep.whatTimeDoYouUsuallyGoToSleep : undefined,
          disabled: false
        }),
        whatTimeDoYouUsuallyGetUp: new UntypedFormControl({ value: miniSphere ? miniSphere.sleep.whatTimeDoYouUsuallyGetUp : undefined, disabled: false }),
        howManyHoursDoYouSleepOnAveragePerNight:
          new UntypedFormControl({ value: miniSphere ? miniSphere.sleep.howManyHoursDoYouSleepOnAveragePerNight : undefined, disabled: false }),
      }),
      // stress listo
      stress: new UntypedFormGroup({
        howOftenHaveFeltThatDifficultiesArePilingUpSoMuchThatCanOvercomeThem: new UntypedFormControl({
          value: miniSphere ? miniSphere.stress.howOftenHaveFeltThatDifficultiesArePilingUpSoMuchThatCanOvercomeThem || 0 : undefined,
          disabled: false
        }),
      }),
      // affective environment listo
      environmentAffectivity: new UntypedFormGroup({
        trustWithFamilyAndFriends: new UntypedFormControl({
          value: miniSphere ? miniSphere.environmentAffectivity.trustWithFamilyAndFriends : undefined,
          disabled: false
        }),
        feelHappyOften: new UntypedFormControl({ value: miniSphere ? miniSphere.environmentAffectivity.feelHappyOften : undefined, disabled: false }),
      }),
      // attitude listo
      attitude: new UntypedFormGroup({
        potentialToReachMyGoals: new UntypedFormControl({ value: miniSphere?.attitude?.potentialToReachMyGoals ?? 1, disabled: false }),
      }),
      // actividades enriquecedoras listo
      enrichingActivities: new UntypedFormGroup({
        hasActivity: new UntypedFormControl({ value: miniSphere ? miniSphere.enrichingActivities.hasActivity : false, disabled: false }),
        activity: new UntypedFormControl({ value: miniSphere ? miniSphere.enrichingActivities.activity : undefined, disabled: false }),
        frequency: new UntypedFormControl({ value: miniSphere?.enrichingActivities?.frequency ?? 0, disabled: false }),
      }),
      // exploración física listo
      nutritionalPhysicalExamination: new UntypedFormGroup({
        observations: new UntypedFormControl({ value: miniSphere ? miniSphere.nutritionalPhysicalExamination.observations : '', disabled: false }),
      }),
      // biochemical listo
      biochemical: new UntypedFormGroup({
        glucose: new UntypedFormControl({ value: miniSphere ? miniSphere.biochemical.glucose : 0, disabled: false }),
        cholesterol: new UntypedFormControl({ value: miniSphere ? miniSphere.biochemical.cholesterol : 0, disabled: false }),
        tg: new UntypedFormControl({ value: miniSphere ? miniSphere.biochemical.tg : 0, disabled: false }),
        hdl: new UntypedFormControl({ value: miniSphere ? miniSphere.biochemical.hdl : 0, disabled: false }),
        ldl: new UntypedFormControl({ value: miniSphere ? miniSphere.biochemical.ldl : 0, disabled: false }),
        hbGlic: new UntypedFormControl({ value: miniSphere ? miniSphere.biochemical.hbGlic : 0, disabled: false }),
        insulin: new UntypedFormControl({ value: miniSphere ? miniSphere.biochemical.insulin : 0, disabled: false }),
        vitaminD3: new UntypedFormControl({ value: miniSphere ? miniSphere.biochemical.vitaminD3 : 0, disabled: false }),
      }),
      // physicalActivity listo
      physicalActivity: new UntypedFormGroup({
        occupation: new UntypedFormControl({ value: miniSphere ? miniSphere.physicalActivity.occupation : '', disabled: false }),
        physicalLevelActivity: new UntypedFormControl({
          value: miniSphere ? miniSphere.physicalActivity.physicalLevelActivity : PhysicalActivityLevelEnum.NO_ACTIVITY,
          disabled: false
        }),
        exerciseIntensity: new UntypedFormControl({ value: miniSphere ? miniSphere.physicalActivity.exerciseIntensity : undefined, disabled: false }),
        exercisePerWeek: new UntypedFormControl({ value: miniSphere ? miniSphere.physicalActivity.exercisePerWeek : undefined, disabled: false }),
        duration: new UntypedFormControl({ value: miniSphere ? miniSphere.physicalActivity.duration : undefined, disabled: false }),
        howManyHoursSpendSittingOrRelaxADay: new UntypedFormControl({
          value: miniSphere ? miniSphere.physicalActivity.howManyHoursSpendSittingOrRelaxADay : undefined,
          disabled: false
        }),
        howManyHoursSpendSittingPerStudySchoolWorkMovingADay: new UntypedFormControl({
          value: miniSphere ? miniSphere.physicalActivity.howManyHoursSpendSittingPerStudySchoolWorkMovingADay : undefined,
          disabled: false
        }),
        howManyStepsWalkADay: new UntypedFormControl({ value: miniSphere ? miniSphere.physicalActivity.howManyStepsWalkADay : undefined, disabled: false }),
      }),
      medicalInheritedFamily: new UntypedFormGroup({
        haveAFamilyMemberWithIllness: new UntypedFormControl({
          value: miniSphere ? miniSphere.medicalInheritedFamily.haveAFamilyMemberWithIllness : false,
          disabled: false
        }),
        medicalInheritedFamilyId: new UntypedFormControl({ value: miniSphere?.medicalInheritedFamily?.medicalInheritedFamilyId ?? undefined, disabled: false }),
        recordMedicalInheritedFamily: new UntypedFormControl({
          value: miniSphere?.medicalInheritedFamily?.recordMedicalInheritedFamily ?? undefined,
          disabled: false
        }),
      }),
      // medica personal listo
      medicalPersonal: new UntypedFormGroup({
        haveOrHaveHadAnyDisease: new UntypedFormControl({ value: miniSphere ? miniSphere.medicalPersonal.haveOrHaveHadAnyDisease : false, disabled: false }),
        medicalPersonalId: new UntypedFormControl({ value: miniSphere?.medicalPersonal?.medicalPersonalId ?? undefined, disabled: false }),
        recordMedicalPersonal: new UntypedFormControl({ value: miniSphere?.medicalPersonal?.recordMedicalPersonal ?? undefined, disabled: false }),
      }),
      // nutricional listo
      nutritionalHistory: new UntypedFormGroup({
        feedingPreference: new UntypedFormControl({ value: miniSphere?.nutritionalHistory?.feedingPreference ?? '', disabled: false }),
        allergicToAnyMedication: new UntypedFormControl({ value: miniSphere?.nutritionalHistory?.allergicToAnyMedication ?? '', disabled: false }),
        planRatingInPeriod: new UntypedFormControl({ value: miniSphere ? miniSphere.nutritionalHistory.planRatingInPeriod : undefined, disabled: false }),
        foodDislikes: new FormArray([]),
        allergies: new FormArray([])
      }),
    });
    this.excludedDislikeIngredients = miniSphere?.nutritionalHistory?.foodDislikes ?? [];
    this.excludedAllergyIngredients = miniSphere?.nutritionalHistory?.allergies ?? [];
    if (miniSphere.enrichingActivities.activity && !this.enrichingActivities.includes(miniSphere.enrichingActivities.activity)) {
      this.enrichingActivities.push(miniSphere.enrichingActivities.activity);
    }
    if (!this.userLogged.experts) {
      this.form.disable();
    }
  }

  sliderFormatter(value: number): string {
    let status = '';
    switch (value) {
      case 1:
        status = 'fuertemente en desacuerdo';
        break;
      case 2:
        status = 'moderadamente en desacuerdo';
        break;
      case 3:
        status = 'Medianamente en desacuerdo';
        break;
      case 4:
        status = 'Medianamente de acuerdo';
        break;
      case 5:
        status = 'Moderadamente de acuerdo';
        break;
      case 6:
        status = 'Fuertemente de acuerdo';
        break;
    }
    return status;
  }

  onAddOtherNutritionalRegimen(input: HTMLInputElement): void {
    const value = input.value;
    if (!value) return;
    if (this.nutritionalRegimen.filter(regimen => regimen.value === value)) {
      this.nutritionalRegimen.pop();
    }
    this.nutritionalRegimen = [...this.nutritionalRegimen, { value: input.value, label: input.value }];
  }

  /**
   * Agrega una nueva actividad enriquecedora
   * @param input elemento html
   */
  onAddOtherEnrichingActivity(input: HTMLInputElement): void {
    const value = input.value;
    if (!value) return;
    if (this.enrichingActivities.filter(regimen => regimen === value)) {
      this.enrichingActivities.pop();
    }
    this.enrichingActivities = [...this.enrichingActivities, value];
  }

  ngOnDestroy(): void {
  }

  async onSelectedDislikeIngredient(ingredient: Partial<IIngredient>): Promise<void> {
    this.excludedDislikeIngredients.push({
      ingredientName: ingredient.name,
      ingredientId: ingredient._id
    });
  }

  onRemoveDislikeIngredient(index: number): void {
    this.excludedDislikeIngredients.splice(index, 1);
  }

  get excludedDislikeIngredientIds(): string[] {
    return this.excludedDislikeIngredients.map((ingredient: MiniSphereNamespace.IIngredient) => ingredient.ingredientId);
  }


  onChangeStartDate(value: Date | null): void {
  }

  onChangeEndDate(value: Date | null): void {
  }

  onOpenModalAddAllergy(): void {
    if (this.excludedAllergyIngredients.length >= PLATFORM_CONFIG.RECORDS.NUTRITIONAL_HISTORY.MAX_ALLERGIES) {
      this.openModalExceedRegisters();
      return;
    }
    this.modalAddAllergyVisible = true;
  }

  openModalExceedRegisters(): void {
    this.modal.confirm({
      nzTitle: '¡Ups parece que has realizado demasidos registros!',
      nzContent: 'Comunicate con DELIE.',
      nzOkText: 'ACEPTAR',
      nzClassName: 'modal-confirm modal-confirm-medium-size',
      nzClosable: false,
      nzOnOk: async () => {
      },
      nzOnCancel: () => {
      }
    });
  }

  onRemoveAllergyIngredient(index: number): void {
    this.excludedAllergyIngredients.splice(index, 1);
  }


  async onSave(): Promise<void> {
    const recordMiniSphere: MiniSphereNamespace.IMiniSphere = this.checkFormProperties(this.form.value);

    if (recordMiniSphere.medicalInheritedFamily.haveAFamilyMemberWithIllness && !this.recordHistoryFamilialMedicalComponent.checkFields()) {
      return;
    }

    try {
      await firstValueFrom(this.store.dispatch(new Loader(true, 'Guardando ...')));

      await firstValueFrom(this.store.dispatch(new RecordMiniSphereAction.PatchActive(
        this.serviceId,
        this.patientId,
        this.recordMiniSphereId,
        recordMiniSphere
      )));
      this.messageService.success('Datos guardados correctamente');
    } catch (e) {
      console.log(e);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  async onCloseModalAddAllergy(allergies: MiniSphereNamespace.IIngredient[]): Promise<void> {
    this.excludedAllergyIngredients = allergies;
    this.modalAddAllergyVisible = false;
  }

  /**
   * Este método comprueba las propiedades del formulario y válida el objeto
   * para poder ser enviadas al servidor.
   * @private
   */
  private checkFormProperties(value: MiniSphereNamespace.IMiniSphere): MiniSphereNamespace.IMiniSphere {
    const form: MiniSphereNamespace.IMiniSphere = _cloneDeep(value);

    // MEDICAL PERSONAL
    form.nutritionalHistory.planRatingInPeriod = form.nutritionalHistory?.planRatingInPeriod ?? 0;

    if (form.medicalPersonal.haveOrHaveHadAnyDisease) {
      form.medicalPersonal.recordMedicalPersonal = _omit(
        this.recordHistoryPersonalMedicalComponent.recordMedicalPersonal,
        ['expertId', 'patientId', 'serviceId']
      );

      // SURGERIES
      if (!form.medicalPersonal.recordMedicalPersonal.hasSurgery) {
        delete form.medicalPersonal.recordMedicalPersonal.surgeries;
      }

      if (!form.medicalPersonal.recordMedicalPersonal.hasProsthesis) {
        delete form.medicalPersonal.recordMedicalPersonal.prosthesisBodyPart;
      }

    } else {
      delete form.medicalPersonal.recordMedicalPersonal;
    }

    // MEDICAL INHERITED FAMILY
    if (form.medicalInheritedFamily.haveAFamilyMemberWithIllness) {
      form.medicalInheritedFamily.recordMedicalInheritedFamily = _omit(this.recordHistoryFamilialMedicalComponent.recordMedicalHerded, ['expertId', 'patientId', 'serviceId']);
    } else {
      delete form.medicalInheritedFamily.recordMedicalInheritedFamily;
    }

    if (form.medicalInheritedFamily.recordMedicalInheritedFamily?.diseaseCategories) {
      delete  form.medicalInheritedFamily.recordMedicalInheritedFamily.diseaseCategories;
    }

    if (form.medicalPersonal.recordMedicalPersonal?.diseaseCategories) {
      delete  form.medicalPersonal.recordMedicalPersonal.diseaseCategories;
    }

    // NUTRITIONAL HISTORY
    form.nutritionalHistory.foodDislikes = this.excludedDislikeIngredients;
    form.nutritionalHistory.allergies = this.excludedAllergyIngredients;
    if (!form.sleep.whatTimeDoYouUsuallyGetUp) form.sleep.whatTimeDoYouUsuallyGetUp = undefined;
    if (!form.sleep.whatTimeDoYouUsuallyGoToSleep) form.sleep.whatTimeDoYouUsuallyGoToSleep = undefined;


    // PHYSCAL ACTIVITY
    form.physicalActivity.exercisePerWeek = form.physicalActivity.exercisePerWeek ?? 0;
    form.physicalActivity.duration = form.physicalActivity.duration ?? 0;
    form.physicalActivity.howManyStepsWalkADay = form.physicalActivity.howManyStepsWalkADay ?? 0;


    return form;
  }
}
